import { Check, Circle, LockKeyhole } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { api } from "../api";
import { useAuth } from "../auth";
import { type ProviderId } from "../brands";
import { Button, CopyButton, Modal, ProviderMark } from "../components";
import { type DependencyGroup, dependencyKindLabels } from "../../shared/dependencies";
import { PROVIDER_IDS } from "../../shared/providers";
import { type HandoffResponse, providerName } from "./loopPlacements";
import { moveChecklist } from "./loopDependencies";

const TARGETS: readonly ProviderId[] = PROVIDER_IDS;

/** "{Target} must provide": every recorded dependency, checked when the target environment has already confirmed it. */
function ProvideChecklist({ target, groups }: { target: ProviderId; groups: DependencyGroup[] }) {
  const name = providerName(target);
  const items = moveChecklist(groups, target);
  const confirmed = items.filter((item) => item.confirmed).length;
  return (
    <section className="move-loop__provide" aria-label={`${name} must provide`}>
      <p className="move-loop__provide-head"><span>{name} must provide</span><span>{items.length ? `${confirmed} of ${items.length} confirmed` : "nothing recorded"}</span></p>
      {items.length > 0 ? (
        <ul className="move-loop__checklist">
          {items.map((item) => (
            <li key={`${item.group.kind}:${item.group.name}`} className={item.confirmed ? "is-confirmed" : ""}>
              {item.confirmed ? <Check size={14} aria-hidden="true" /> : <Circle size={14} aria-hidden="true" />}
              <span className="sr-only">{item.confirmed ? "Confirmed" : "Not confirmed"}</span>
              <span className="move-loop__checklist-name"><b>{item.group.name}</b>{!item.group.required ? <i>optional</i> : null}{item.group.ref ? <code title={item.group.ref}>{item.group.ref}</code> : null}</span>
              <small>{item.confirmed ? `confirmed by ${name}` : dependencyKindLabels[item.group.kind]}</small>
            </li>
          ))}
        </ul>
      ) : (
        <p className="move-loop__provide-empty">No dependencies recorded. The prompt asks {name} to report what it needs before the first run.</p>
      )}
    </section>
  );
}

/**
 * Plans a move of one loop to another agent. Picks the target, takes an optional environment note,
 * records a planned placement, and shows the handoff prompt to paste into the target agent.
 * The prompt carries no keys; the target uses the LoopGroup connection already in its environment.
 */
export function MoveLoopModal({ open, onClose, loop, currentProvider, onPlanned, dependencies = [] }: { open: boolean; onClose: () => void; loop: { id: string; name: string }; currentProvider: ProviderId | null; onPlanned: () => void; dependencies?: DependencyGroup[] }) {
  const { activeOrganization } = useAuth();
  const organizationId = activeOrganization?.id;
  const [target, setTarget] = useState<ProviderId | null>(null);
  const [environment, setEnvironment] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<HandoffResponse | null>(null);

  useEffect(() => {
    if (!open) return;
    setTarget(null);
    setEnvironment("");
    setBusy(false);
    setError("");
    setResult(null);
  }, [open]);

  async function plan(event: FormEvent) {
    event.preventDefault();
    if (!organizationId || !target) return;
    setBusy(true);
    setError("");
    try {
      const response = await api<HandoffResponse>(`/api/app/loops/${loop.id}/placements`, { method: "POST", body: JSON.stringify({ to: target, environment: environment.trim() || undefined }) }, organizationId);
      setResult(response);
      onPlanned();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The move could not be planned.");
    } finally {
      setBusy(false);
    }
  }

  const targetName = target ? providerName(target) : "the new agent";
  const fromName = currentProvider ? providerName(currentProvider) : null;

  return (
    <Modal open={open} title="Move to another agent" onClose={onClose}>
      {result ? (
        <div className="move-loop__result">
          <p className="move-loop__ready" role="status"><Check size={15} aria-hidden="true" /> Move to {targetName} planned. It turns active on the first run {targetName} reports for this loop.</p>
          <pre className="move-loop__prompt" tabIndex={0} aria-label={`Handoff prompt for ${targetName}`}>{result.handoff}</pre>
          <div className="move-loop__copy"><CopyButton value={result.handoff} label="Copy prompt" /></div>
          <ProvideChecklist target={result.placement.provider} groups={dependencies} />
          <ol className="move-loop__steps">
            <li><b>Send the prompt</b> to {targetName} in the environment where the loop will run. It proposes a plan first and changes nothing until you approve.</li>
            <li><b>The placement turns active</b> when {targetName} reports its first run for this same loop. Until then it shows as planned under Runs on.</li>
            <li><b>Retire the old one here.</b> {fromName ? `The ${fromName} placement is retired the moment ${targetName}'s first run arrives; stop the ${fromName} schedule yourself, then use Retire under Performance by agent for anything left over.` : "Any other active placement is retired the moment the first run arrives; stop the old schedule yourself, then use Retire under Performance by agent for anything left over."}</li>
          </ol>
          <p className="move-loop__note"><LockKeyhole size={14} aria-hidden="true" /> The prompt contains no keys. {targetName} uses the LoopGroup connection already in its environment.</p>
          <div className="modal__actions"><Button type="button" onClick={onClose}>Done</Button></div>
        </div>
      ) : (
        <form className="form-stack move-loop" onSubmit={(event) => void plan(event)}>
          <p className="move-loop__intro">{loop.name} keeps its identity, history, notes, and cards. Moving creates a new placement; it never creates a second loop.</p>
          <fieldset className="move-loop__targets">
            <legend>Move to</legend>
            {TARGETS.map((id) => {
              const current = id === currentProvider;
              return (
                <label key={id} className={`move-loop__target ${target === id ? "is-selected" : ""} ${current ? "is-current" : ""}`.trim()}>
                  <input type="radio" name="target" value={id} checked={target === id} disabled={current} onChange={() => setTarget(id)} />
                  <ProviderMark provider={id} size={30} />
                  <span><b>{providerName(id)}</b>{current ? <small>Runs here now</small> : null}</span>
                  {target === id ? <Check size={15} aria-hidden="true" /> : null}
                </label>
              );
            })}
          </fieldset>
          {target ? <ProvideChecklist target={target} groups={dependencies} /> : null}
          <label>
            <span>Environment note</span>
            <small>Where it will run: scheduler, repo, machine. Optional, and included in the prompt.</small>
            <input value={environment} onChange={(event) => setEnvironment(event.target.value)} placeholder="Claude Code scheduled task on the finance laptop" maxLength={240} />
          </label>
          {error ? <div className="form-error" role="alert">{error}</div> : null}
          <div className="modal__actions"><Button type="button" variant="ghost" onClick={onClose}>Cancel</Button><Button type="submit" busy={busy} disabled={!target}>Plan the move</Button></div>
        </form>
      )}
    </Modal>
  );
}
